import { memo, useCallback } from 'react'
import { IconButton, Modal as MuiModal, SxProps } from '@mui/material'
import { CrossThinIcon } from '@/assets'
import { useDeviceType } from '@/utils'
import { Box } from '@/components/Layout'
import { Text } from '@/components/Data'
import { ActionButtons } from '@/components/Inputs'
import { modalStyles as styles } from './Modal.style'
import { ModalCloseReason, ModalProps } from './Modal.types'
import clsx from 'clsx'
import { modalClasses } from './Modal.data'
import BottomDrawer from '../BottomDrawer'

const Modal = ({
  open,
  containerStyles,
  HeaderIcon,
  showCloseIcon = false,
  onClose,
  title,
  description,
  children,
  disableBackdropClick = false,
  titleTextProps,
  descriptionTextProps,
  headerProps,
  crossIconStyles,
  slotProps = {},
  showTitleOnTop = false,
  actionButtonProps,
  ...props
}: ModalProps) => {
  const { isMobile } = useDeviceType()

  const handleClose = useCallback(
    (event: object, reason: ModalCloseReason) => {
      if (reason === 'backdropClick' && disableBackdropClick) return
      onClose?.(event, reason)
    },
    [disableBackdropClick, onClose],
  )

  if (isMobile) {
    return (
      <BottomDrawer
        open={open}
        onOpen={() => {}}
        onClose={(event) => handleClose(event, 'crossIconClick')}
        className={clsx({
          [modalClasses.titleOnTop]: showTitleOnTop,
        })}
        slotProps={{
          backdrop: {
            sx: styles.backdrop,
            ...slotProps.backdrop,
          },
          ...slotProps,
        }}
        actionButtonProps={actionButtonProps}
        hideHeading>
        <Box sx={[styles.container, containerStyles] as SxProps}>
          <RenderModalContent
            HeaderIcon={HeaderIcon}
            showCloseIcon={showCloseIcon}
            onClose={handleClose}
            title={title}
            titleTextProps={titleTextProps}
            showTitleOnTop={showTitleOnTop}
            description={description}
            descriptionTextProps={descriptionTextProps}
            headerProps={headerProps}
            crossIconStyles={crossIconStyles}>
            {children}
          </RenderModalContent>
        </Box>
      </BottomDrawer>
    )
  }

  return (
    <MuiModal
      open={open}
      onClose={handleClose}
      className={clsx({
        [modalClasses.titleOnTop]: showTitleOnTop,
      })}
      slotProps={{
        backdrop: {
          sx: styles.backdrop,
          ...slotProps.backdrop,
        },
        ...slotProps,
      }}
      {...props}>
      <Box sx={[styles.modal, styles.container, containerStyles] as SxProps}>
        <RenderModalContent
          HeaderIcon={HeaderIcon}
          showCloseIcon={showCloseIcon}
          onClose={handleClose}
          title={title}
          titleTextProps={titleTextProps}
          showTitleOnTop={showTitleOnTop}
          description={description}
          descriptionTextProps={descriptionTextProps}
          headerProps={headerProps}
          crossIconStyles={crossIconStyles}>
          {children}
        </RenderModalContent>
        {actionButtonProps && <ActionButtons {...actionButtonProps} />}
      </Box>
    </MuiModal>
  )
}

export default memo(Modal)

const RenderModalContent = ({
  HeaderIcon,
  showCloseIcon,
  title,
  showTitleOnTop = false,
  description,
  children,
  titleTextProps = {},
  descriptionTextProps = {},
  headerProps = {},
  crossIconStyles,
  onClose,
}: Partial<ModalProps>) => {
  const { sx: titleTextSx, ...otherTitleTextProps } = titleTextProps
  const { sx: descriptionTextSx, ...otherDescriptionTextProps } = descriptionTextProps
  const { sx: headerSx, ...headerOtherProps } = headerProps

  const shouldShowTitleSection = (title && !showTitleOnTop) || description

  return (
    <>
      <Box
        sx={[styles.header, !HeaderIcon && styles.headerWithoutIcon, headerSx] as SxProps}
        {...headerOtherProps}>
        {HeaderIcon && <> {HeaderIcon} </>}

        {title && showTitleOnTop && (
          <Text
            component={'h2'}
            sx={[styles.title, titleTextSx] as SxProps}
            {...otherTitleTextProps}>
            {title}
          </Text>
        )}

        {showCloseIcon && (
          <IconButton
            sx={[styles.crossIcon, crossIconStyles] as SxProps}
            onClick={(event) => onClose?.(event, 'crossIconClick')}>
            <CrossThinIcon className="crossIcon" />
          </IconButton>
        )}
      </Box>

      {shouldShowTitleSection && (
        <Box>
          {title && !showTitleOnTop && (
            <Text
              component={'h2'}
              sx={[styles.title, titleTextSx] as SxProps}
              {...otherTitleTextProps}>
              {title}
            </Text>
          )}
          {description && (
            <Text
              component={'p'}
              sx={[styles.description, descriptionTextSx] as SxProps}
              {...otherDescriptionTextProps}>
              {description}
            </Text>
          )}
        </Box>
      )}
      {children}
    </>
  )
}
